import { normalizeScannerPayload } from '../components/shared/barcodeScanner';

export const NOT_RACERANGER_DEVICE_SCAN_TOAST =
  "Could not read that Raceranger device QR. Check the scan and try again.";

export const SCAN_DEVICE_EXPECT_BIB_FIRST =
  'Scan the athlete number first, not a device QR.';

export const SCAN_DEVICE_INVALID_FORMAT =
  'Unrecognized scan - ensure the device has been added to the device mappings table. Scan a Raceranger device QR or use a device ID like Rear_1234A.';

// Fleet IDs can include mixed alphanumerics after the prefix, e.g. Rear_3P79G.
export const FLEET_DEVICE_ID_RE = /^(Rear|Front)_[A-Za-z0-9]+$/i;

function isRacerangerCoreToken(value: string): boolean {
  return /^(?=.*\d)(?=.*[A-Za-z])[A-Za-z0-9]+$/.test(value);
}

function canonicalizeFleetDeviceId(raw: string): string | null {
  const cleaned = normalizeScannerPayload(raw);
  if (!FLEET_DEVICE_ID_RE.test(cleaned)) return null;
  const match = /^(Rear|Front)_([A-Za-z0-9]+)$/i.exec(cleaned);
  if (!match) return null;
  const prefix = match[1].toLowerCase() === 'rear' ? 'Rear' : 'Front';
  const suffix = match[2].toUpperCase();
  return `${prefix}_${suffix}`;
}

/** Pull `device=` from a scan payload, or accept a bare `1138Cr` / `1138C`-style token. */
export function extractRacerangerDeviceToken(raw: string): string | null {
  const cleaned = normalizeScannerPayload(raw);
  if (!cleaned) return null;

  const deviceParam = cleaned.match(/device=([^&#\s]+)/i);
  if (deviceParam?.[1]) {
    try {
      return decodeURIComponent(deviceParam[1].trim());
    } catch {
      return deviceParam[1].trim();
    }
  }

  if (/^(?=.*\d)(?=.*[A-Za-z])[A-Za-z0-9]+[rRfF]?$/i.test(cleaned)) return cleaned;
  return null;
}

/**
 * QR device code → core id (digits + position letter).
 * `1138Cr` → `1138C` (drop trailing `r` rear marker when present).
 * `1138C` → `1138C` (no trailing `r` in URL).
 */
export function racerangerDeviceTokenToCoreId(token: string): string | null {
  const t = token.trim();
  const withTrailingRole = /^([A-Za-z0-9]+)([rRfF])$/.exec(t);
  if (withTrailingRole && isRacerangerCoreToken(withTrailingRole[1])) {
    return withTrailingRole[1].toUpperCase();
  }
  if (isRacerangerCoreToken(t)) {
    return t.toUpperCase();
  }
  return null;
}

function fleetIdFromToken(token: string, fallbackRole?: 'rear' | 'front'): string | null {
  const trimmed = token.trim();

  if (fallbackRole) {
    const expectedSuffix = fallbackRole === 'rear' ? 'r' : 'f';
    if (trimmed.length > 1 && trimmed.toLowerCase().endsWith(expectedSuffix)) {
      const coreFromSuffix = trimmed.slice(0, -1);
      if (isRacerangerCoreToken(coreFromSuffix)) {
        const prefix = fallbackRole === 'rear' ? 'Rear' : 'Front';
        return `${prefix}_${coreFromSuffix.toUpperCase()}`;
      }
    }
  }

  const withRoleSuffix = /^([A-Za-z0-9]+)([rRfF])$/.exec(trimmed);
  if (withRoleSuffix && isRacerangerCoreToken(withRoleSuffix[1])) {
    const prefix = withRoleSuffix[2].toLowerCase() === 'f' ? 'Front' : 'Rear';
    return `${prefix}_${withRoleSuffix[1].toUpperCase()}`;
  }
  const core = racerangerDeviceTokenToCoreId(trimmed);
  if (!core || !fallbackRole) return null;
  const prefix = fallbackRole === 'rear' ? 'Rear' : 'Front';
  return `${prefix}_${core}`;
}

/** True when input looks like a Raceranger QR payload. */
export function looksLikeRacerangerDeviceScanAttempt(raw: string): boolean {
  const cleaned = normalizeScannerPayload(raw);
  if (!cleaned) return false;
  return (
    /raceranger/i.test(cleaned) ||
    /instructions/i.test(cleaned) ||
    /device=/i.test(cleaned) ||
    /^\d+[A-Za-z][rRfF]?$/i.test(cleaned)
  );
}

export function resolveRacerangerScannedDeviceId(
  raw: string,
  role: 'rear' | 'front',
): { ok: true; deviceId: string } | { ok: false } {
  const cleaned = normalizeScannerPayload(raw);
  if (!cleaned) return { ok: false };

  const canonicalFleetId = canonicalizeFleetDeviceId(cleaned);
  if (canonicalFleetId) {
    return { ok: true, deviceId: canonicalFleetId };
  }

  const token = extractRacerangerDeviceToken(cleaned);
  if (!token) return { ok: false };

  const deviceId = fleetIdFromToken(token, role);
  if (!deviceId) return { ok: false };

  return { ok: true, deviceId };
}

/**
 * Role-free resolver: infers Rear/Front from the trailing `r`/`f` suffix on the
 * QR token (e.g. `1138Cr` -> `Rear_1138C`, `1138Cf` -> `Front_1138C`).
 */
export function resolveRacerangerScannedDeviceIdAuto(
  raw: string,
): { ok: true; deviceId: string; converted: boolean } | { ok: false } {
  const cleaned = normalizeScannerPayload(raw);
  if (!cleaned) return { ok: false };

  const canonicalFleetId = canonicalizeFleetDeviceId(cleaned);
  if (canonicalFleetId) {
    return { ok: true, deviceId: canonicalFleetId, converted: false };
  }

  const token = extractRacerangerDeviceToken(cleaned);
  if (!token) return { ok: false };

  const deviceId = fleetIdFromToken(token);
  if (!deviceId) return { ok: false };

  return { ok: true, deviceId, converted: true };
}

export type ResolveFleetDeviceScanResult =
  | { ok: true; deviceId: string }
  | { ok: false; reason: 'empty' | 'expect_bib' | 'raceranger_unparsed' | 'invalid_format' };

/**
 * Resolve a packaging / retrieval scan to a fleet device id (QR URL or Rear_1234A).
 */
export function resolveFleetDeviceScan(
  raw: string,
  options?: { role?: 'rear' | 'front'; expectBib?: boolean },
): ResolveFleetDeviceScanResult {
  const cleaned = normalizeScannerPayload(raw);
  if (!cleaned) return { ok: false, reason: 'empty' };

  if (options?.expectBib && looksLikeRacerangerDeviceScanAttempt(cleaned)) {
    return { ok: false, reason: 'expect_bib' };
  }

  const canonicalFleetId = canonicalizeFleetDeviceId(cleaned);
  if (canonicalFleetId) {
    return { ok: true, deviceId: canonicalFleetId };
  }

  if (options?.role) {
    const resolved = resolveRacerangerScannedDeviceId(cleaned, options.role);
    if (resolved.ok) return { ok: true, deviceId: resolved.deviceId };
  }

  const auto = resolveRacerangerScannedDeviceIdAuto(cleaned);
  if (auto.ok) return { ok: true, deviceId: auto.deviceId };

  if (looksLikeRacerangerDeviceScanAttempt(cleaned)) {
    return { ok: false, reason: 'raceranger_unparsed' };
  }

  return { ok: false, reason: 'invalid_format' };
}

/**
 * Match a scan against assigned device ids (handles QR without r/f suffix).
 */
export function resolveFleetDeviceScanForAssignedDevices(
  raw: string,
  assignedDeviceIds: ReadonlySet<string>,
): string | null {
  const cleaned = normalizeScannerPayload(raw);
  if (!cleaned) return null;

  const candidates: string[] = [];
  const push = (id: string): void => {
    const v = id.trim();
    if (v && !candidates.includes(v)) candidates.push(v);
  };

  push(cleaned);
  const auto = resolveRacerangerScannedDeviceIdAuto(cleaned);
  if (auto.ok) push(auto.deviceId);

  const token = extractRacerangerDeviceToken(cleaned);
  if (token) {
    const fromToken = fleetIdFromToken(token);
    if (fromToken) push(fromToken);
    const core = racerangerDeviceTokenToCoreId(token);
    if (core) {
      push(`Rear_${core}`);
      push(`Front_${core}`);
    }
  }

  const assignedByLower = new Map<string, string>();
  for (const assignedId of assignedDeviceIds) {
    const canonicalAssigned = canonicalizeFleetDeviceId(assignedId) ?? assignedId.trim();
    if (!canonicalAssigned) continue;
    assignedByLower.set(canonicalAssigned.toLowerCase(), canonicalAssigned);
  }

  for (const id of candidates) {
    const canonicalCandidate = canonicalizeFleetDeviceId(id) ?? id.trim();
    if (!canonicalCandidate) continue;
    const matched = assignedByLower.get(canonicalCandidate.toLowerCase());
    if (matched) return matched;
  }
  return null;
}

/**
 * Convenience: returns the resolved fleet device id if the input is a
 * Raceranger QR scan, otherwise returns the input untouched.
 */
export function maybeConvertRacerangerScan(raw: string): string {
  const auto = resolveRacerangerScannedDeviceIdAuto(raw);
  if (auto.ok) return auto.deviceId;
  const resolved = resolveFleetDeviceScan(raw);
  if (resolved.ok) return resolved.deviceId;
  return raw;
}

export function fleetDeviceScanErrorMessage(
  reason: Exclude<ResolveFleetDeviceScanResult, { ok: true }>['reason'],
): string {
  switch (reason) {
    case 'expect_bib':
      return SCAN_DEVICE_EXPECT_BIB_FIRST;
    case 'raceranger_unparsed':
      return NOT_RACERANGER_DEVICE_SCAN_TOAST;
    case 'invalid_format':
      return SCAN_DEVICE_INVALID_FORMAT;
    default:
      return SCAN_DEVICE_INVALID_FORMAT;
  }
}
